import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { provideComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { first, orderBy } from 'lodash-es';
import { DocumentViewerActions } from 'projects/semeris/src/store/document-viewer/document-viewer.actions';
import { LabelSelectors } from 'projects/semeris/src/store/label/label.selectors';
import { TransactionSelectors } from 'projects/semeris/src/store/transaction/transaction.selectors';
import { combineLatest, concat, merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { ParagraphViewerSelectedEvent } from '../../../deal/models/deal-output-events';
import { ViewerCommand } from '../../../document-viewer/models/document-viewer.models';
import { SourceReference } from '../../../shared/models/common/source-reference.model';
import { HelpTriggerDirective } from '../../../help/directives/help-trigger.directive';
import { Label } from '../../../label/models/label.model';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { FastTooltipDirective } from '../../../shared/directives/fast-tooltip.directive';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { WebSocketEntities } from '../../../shared/models/web-socket/web-socket.models';
import { WebSocketService } from '../../../shared/services/web-socket/web-socket.service';
import { IconButtonDirective } from '../../../ui/button/icon-button/icon-button.directive';
import { CommentInputComponent } from '../../components/comment-input/comment-input.component';
import { ConversationActivityListComponent } from '../../components/conversation-activity-list/conversation-activity-list.component';
import {
  ConversationInputComponent,
  ConversationSendEvent,
} from '../../components/conversation-input/conversation-input.component';
import { ConversationAnchor, ConversationViewModelWithLabels } from '../../models/conversation.model';
import { CommentService } from '../../services/comment.service';
import { ConversationService } from '../../services/conversation.service';
import { ConversationSidebarStore } from '../../store/conversation-sidebar.store';

@Component({
  selector: 'semeris-conversation-sidebar',
  imports: [
    CommonModule,
    ConversationActivityListComponent,
    ConversationInputComponent,
    MatIconModule,
    CommentInputComponent,
    HelpTriggerDirective,
    IconButtonDirective,
    HeaderComponent,
    LoaderComponent,
    PermissionsDirective,
    FastTooltipDirective,
  ],
  templateUrl: './conversation-sidebar.component.html',
  styleUrls: ['./conversation-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideComponentStore(ConversationSidebarStore)],
})
export class ConversationSidebarComponent implements OnInit, OnDestroy {
  private readonly conversationService = inject(ConversationService);
  private readonly wsService = inject(WebSocketService);
  private readonly commentService = inject(CommentService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(ConversationSidebarStore);
  private readonly globalStore = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  @HostListener('window:visibilitychange')
  public onVisibilityChange() {
    this.visibility$.next(document.visibilityState);
  }

  public readonly Permission = Permission;

  public readonly loading$ = this.store.loading$;

  public readonly reloadConversation$ = new Subject<void>();
  public readonly visibility$ = new Subject<'visible' | 'hidden'>();

  public conversations$: Observable<ConversationViewModelWithLabels[]> = this.store.viewModels$.pipe(
    map((vms) => orderBy(vms, 'createdAt', 'asc'))
  );

  public anchors$: Observable<ConversationAnchor[]> = this.store.anchors$;

  public labels$ = this.store.labels$;
  public users$ = this.store.users$;
  public placeholderMessage$ = this.store.placeholderMessage$;

  public pendingConversation$ = this.store.pendingConversation$;

  public ngOnInit(): void {
    merge(
      concat(of(null), this.router.events.pipe(filter((event) => event instanceof NavigationEnd))),
      this.reloadConversation$,
      this.visibility$.pipe(
        debounceTime(500),
        filter((visible) => visible === 'visible')
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.store.loadByLocation();
      });

    this.anchors$
      .pipe(
        filter((anchors) => !!anchors?.length),
        switchMap((anchors) => {
          return this.conversationService.listenOnChanges$(anchors.map((a) => a.id));
        }),
        debounceTime(200),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.reloadConversation$.next(undefined);
      });

    this.wsService
      .subscribeToFallbackSignal()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reloadConversation$.next(undefined);
      });
  }

  public onClose() {
    this.store.close();

    if (this.activatedRoute.snapshot.queryParamMap.has('conversationId')) {
      this.router.navigate([], {
        queryParams: { ...this.activatedRoute.snapshot.queryParams, conversationId: undefined },
      });
    }
  }

  public onParagraphSelected(event: ParagraphViewerSelectedEvent) {
    this.globalStore
      .select(TransactionSelectors.transactionByTransactionId(event.transactionId))
      .pipe(take(1))
      .subscribe((transaction) => {
        this.globalStore.dispatch(
          DocumentViewerActions.SetLinkByTransaction({
            transaction,
            documentId: event.paragraph.documentShortName,
          })
        );

        this.globalStore.dispatch(
          DocumentViewerActions.OpenDocument({
            documentId: event.paragraph.documentShortName,
            transactionId: event.transactionId,
          })
        );

        this.globalStore.dispatch(
          DocumentViewerActions.SendCommand({
            message: event.searchFor
              ? {
                  value: event.searchFor,
                  commandType: ViewerCommand.UpdateSearch,
                }
              : {
                  commandType: ViewerCommand.UpdateSearch,
                  value: event.paragraph?.text,
                },
            documentId: event.paragraph.documentShortName,
          })
        );
      });
  }

  public onShowSourceReferences(sourceReferences: SourceReference[]) {
    const ref = first(sourceReferences);

    this.globalStore
      .select(TransactionSelectors.transactionByTransactionId(ref.libraryTransactionId))
      .pipe(take(1))
      .subscribe((transaction) => {
        if (ref.documentShortName) {
          this.globalStore.dispatch(
            DocumentViewerActions.SetLinkByTransaction({
              transaction,
              documentId: ref.documentShortName,
            })
          );

          this.globalStore.dispatch(
            DocumentViewerActions.OpenDocument({
              documentId: ref.documentShortName,
              transactionId: ref.libraryTransactionId,
            })
          );
        } else {
          this.globalStore.dispatch(DocumentViewerActions.OpenDocument({ transactionId: transaction.transactionId }));
        }
      });

    this.globalStore.dispatch(
      DocumentViewerActions.SendCommand({
        message: {
          commandType: ViewerCommand.SourceReference,
          value: sourceReferences,
        },
        documentId: first(sourceReferences)?.documentShortName,
      })
    );
  }

  public onComment(comment: string): void {
    combineLatest([this.anchors$, this.globalStore.select(LabelSelectors.defaultLabelIds)])
      .pipe(
        take(1),
        switchMap(([anchors, labelIds]) => {
          const conversationId = uuid();

          return this.commentService.addComment(
            {
              entity: {
                html: comment,
                markdown: comment,
                conversationId,
              },
            },

            anchors?.length
              ? {
                  contextSource: this.conversationService.getContextLocationByRoute(window.location.pathname),
                  anchors: anchors.filter((a) => a?.snapshot?.displayName && a?.id),
                  conversationId,
                  labels: labelIds,
                }
              : {
                  contextSource: this.conversationService.getContextLocationByRoute(window.location.pathname),
                  conversationId,
                  labels: labelIds,
                }
          );
        })
      )
      .subscribe(() => {
        this.reloadConversation$.next(undefined);
      });
  }

  public onConversationComment(ev: ConversationSendEvent) {
    this.store.onComment(ev);
  }

  public onDiscard() {
    this.store.resetPendingConversation();
  }

  public onLabelCreated(label: Label) {
    this.store.addLabel(label);
  }

  public onRefreshList() {
    this.store.loadList();
  }

  public ngOnDestroy() {
    this.wsService.unsubscribe(WebSocketEntities.Conversation);
  }
}
